import type { SnapshotSeries } from '@prm/shared';
import { PanelHead } from './Panel';

interface StripProps {
  /** Formatter for the latest value. */
  format: (v: number) => string;
  label: string;
  values: Array<number | null>;
  /** Optional reference level drawn as a dashed rule (a band edge, a target). */
  reference?: number | null;
  /** True when higher is worse, so the line colour tracks meaning not direction. */
  higherIsWorse?: boolean;
}

/**
 * One measure over the recorded sessions.
 *
 * Deliberately a bare polyline with no axis: the question this answers is "which
 * way has this been going", and a reader who needs the number reads the value
 * beside it. A gap in the series is a gap in the line rather than an interpolated
 * segment — a missing observation is not a measurement.
 */
export function SparkStrip({ label, values, format, reference, higherIsWorse }: StripProps) {
  const points = values
    .map((v, i) => ({ v, i }))
    .filter((p): p is { v: number; i: number } => p.v !== null && Number.isFinite(p.v));

  if (points.length < 2) {
    return (
      <div className="spark">
        <span className="metric-label">{label}</span>
        <span className="spark__empty">{points.length === 0 ? 'no history yet' : '1 observation'}</span>
      </div>
    );
  }

  const vals = points.map((p) => p.v);
  const refs = reference === null || reference === undefined ? [] : [reference];
  const min = Math.min(...vals, ...refs);
  const max = Math.max(...vals, ...refs);
  const span = max - min || Math.abs(max) || 1;

  const W = 100;
  const H = 26;
  const n = values.length - 1 || 1;
  const x = (i: number) => (i / n) * W;
  const y = (v: number) => H - ((v - min) / span) * H;

  // Split into runs of consecutive observations so gaps stay gaps.
  const runs: Array<Array<{ v: number; i: number }>> = [];
  let run: Array<{ v: number; i: number }> = [];
  for (const p of points) {
    if (run.length > 0 && p.i !== (run[run.length - 1] as { i: number }).i + 1) {
      runs.push(run);
      run = [];
    }
    run.push(p);
  }
  if (run.length > 0) runs.push(run);

  const last = points[points.length - 1] as { v: number };
  const first = points[0] as { v: number };
  const rising = last.v > first.v;
  const worsening = higherIsWorse ? rising : !rising;

  return (
    <div className="spark">
      <span className="metric-label">{label}</span>
      <svg className="spark__svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        {reference !== null && reference !== undefined ? (
          <line x1="0" x2={W} y1={y(reference)} y2={y(reference)} className="spark__ref" />
        ) : null}
        {runs
          .filter((r) => r.length >= 2)
          .map((r, idx) => (
            <polyline
              key={idx}
              className={`spark__line ${worsening ? 'is-worse' : 'is-better'}`}
              points={r.map((p) => `${x(p.i)},${y(p.v)}`).join(' ')}
            />
          ))}
        {runs
          .filter((r) => r.length === 1)
          .map((r, idx) => (
            <circle
              key={`d${idx}`}
              className="spark__dot"
              cx={x((r[0] as { i: number }).i)}
              cy={y((r[0] as { v: number }).v)}
              r="1.4"
            />
          ))}
      </svg>
      <div className="spark__value">{format(last.v)}</div>
    </div>
  );
}

/** Three measures over the recorded sessions, each against its own limit. */
export function HistoryPanel({
  series,
  bandMax,
  volTarget,
  maxDrawdown,
}: {
  series: SnapshotSeries;
  bandMax: number;
  volTarget: number;
  maxDrawdown: number;
}) {
  const sessions = series.takenAt.length;

  return (
    <section className="panel">
      <PanelHead
        title="History"
        description="Recorded sessions. The dashed rule is the limit, not data."
        sub={
          <span className="panel__sub">
            {sessions === 0 ? 'no sessions recorded' : `${sessions} session${sessions === 1 ? '' : 's'}`}
          </span>
        }
      />

      <div className="spark-grid">
        <SparkStrip
          label="Beta-adj net"
          values={series.betaAdjNet}
          reference={bandMax}
          higherIsWorse
          format={(v) => `${v.toFixed(2)}×`}
        />
        <SparkStrip
          label="Realized vol"
          values={series.realizedVol}
          reference={volTarget}
          higherIsWorse
          format={(v) => `${(v * 100).toFixed(2)}%`}
        />
        <SparkStrip
          label="Worst stress"
          values={series.worstStress}
          reference={-maxDrawdown}
          format={(v) => `${(v * 100).toFixed(1)}%`}
        />
      </div>
    </section>
  );
}
